import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { useLanguage } from '../contexts/LanguageContext';
import { authService } from '../services/auth';

// Toggle to re-enable Google sign-in. Hidden during early access while the
// OAuth flow is being finalized — flip back to true to surface the buttons.
const SHOW_GOOGLE_AUTH = false;

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, loading, isAuthenticated, loadUser } = useAuthStore();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();

  // Handle Google OAuth callback
  useEffect(() => {
    const token = searchParams.get('token');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(language === 'tr' ? 'Google ile giriş iptal edildi' : 'Google login cancelled');
      return;
    }

    if (token) {
      localStorage.setItem('access_token', token);
      loadUser().then(() => {
        navigate('/dashboard', { replace: true });
      }).catch((err: any) => {
        console.error('Failed to load user after Google login:', err);
        setError(language === 'tr' ? 'Kullanıcı bilgileri yüklenemedi' : 'Failed to load user info');
      });
    }
  }, [searchParams, navigate, loadUser, language]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const authUrl = await authService.getGoogleAuthUrl();
      window.location.href = authUrl;
    } catch (err: any) {
      console.error('Google login error:', err);
      let errorMsg = language === 'tr' ? 'Google ile giriş başlatılamadı' : 'Failed to initiate Google login';
      if (err?.response?.status === 500 || err?.message?.includes('not configured')) {
        errorMsg = language === 'tr'
          ? 'Google OAuth yapılandırılmamış. Lütfen sistem yöneticisine başvurun.'
          : 'Google OAuth is not configured. Please contact the system administrator.';
      } else if (err?.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      setError(errorMsg);
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(username, password);
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 200);
    } catch (err: any) {
      let errorMessage = t.loginError;

      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        errorMessage = language === 'tr'
          ? 'Sunucu yanıt vermedi (timeout). Lütfen tekrar deneyin.'
          : 'Server timeout. Please try again.';
      } else if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network Error') || err?.message?.includes('Failed to fetch')) {
        errorMessage = language === 'tr'
          ? 'Sunucuya bağlanılamadı.'
          : 'Cannot reach server.';
      } else if (err?.response?.status === 401) {
        errorMessage = err?.response?.data?.detail || t.loginError;
      } else if (err?.response?.status === 422) {
        errorMessage = language === 'tr' ? 'Geçersiz istek formatı.' : 'Invalid request.';
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-background flex relative overflow-hidden">
      {/* Editorial side panel — hidden on mobile */}
      <aside className="hidden lg:flex relative w-[44%] min-h-screen border-r border-hairline flex-col justify-between p-12 xl:p-16">
        {/* Top row: brand + scene slug */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            aria-label="Mr.Mirror"
          >
            <img
              src="/mr-mirror-logo.png"
              alt=""
              className="w-9 h-9 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-serif italic text-2xl text-ink leading-none">
              Mr.Mirror
              <sup className="font-mono not-italic text-[10px] text-accent ml-0.5 tracking-wider">®</sup>
            </span>
          </button>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-mute">
            INT. — GİRİŞ
          </span>
        </div>

        {/* Centerpiece quote */}
        <div className="max-w-md">
          <span className="eyebrow mb-6 block">/ DİNLEME EVRESİ</span>
          <h2 className="display-h display-md mb-6">
            {language === 'tr' ? (
              <>
                Senaryon, <em>aynaya</em><br/>
                tutuluyor.
              </>
            ) : (
              <>
                Your script, <em>held up</em><br/>
                to the mirror.
              </>
            )}
          </h2>
          <p className="font-prose text-[15px] text-ink-dim leading-relaxed max-w-sm">
            {language === 'tr'
              ? 'Hesabınla giriş yap. Yeni bir analiz başlat ya da mevcut analizlerine devam et.'
              : 'Sign in to your account. Start a new analysis or continue an existing one.'}
          </p>
        </div>

        {/* Bottom mono caption */}
        <div className="flex items-center justify-between text-ink-mute">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">/ AUTH · 0001</span>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">v1.0</span>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex flex-col justify-center items-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile brand mark */}
          <button
            onClick={() => navigate('/')}
            className="lg:hidden flex items-center gap-3 mb-12 hover:opacity-80 transition-opacity"
          >
            <img
              src="/mr-mirror-logo.png"
              alt=""
              className="w-9 h-9 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-serif italic text-2xl text-ink leading-none">
              Mr.Mirror
              <sup className="font-mono not-italic text-[10px] text-accent ml-0.5 tracking-wider">®</sup>
            </span>
          </button>

          {/* Heading */}
          <span className="eyebrow mb-4 block">/ 01 · {language === 'tr' ? 'Kimlik Doğrulama' : 'Authentication'}</span>
          <h1 className="display-h display-md mb-3">
            {language === 'tr' ? <>Tekrar <em>hoşgeldin.</em></> : <>Welcome <em>back.</em></>}
          </h1>
          <p className="font-prose text-[15px] text-ink-dim leading-relaxed mb-10">
            {t.accountLogin}
          </p>

          {/* Error band */}
          {error && (
            <div className="mb-8 p-4 border border-signal-red/40 bg-signal-red/10">
              <span className="eyebrow-mute mb-1 block">/ ERROR</span>
              <p className="font-prose text-[14px] text-signal-red leading-relaxed">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-7">
            <div>
              <label className="field-label">
                {language === 'tr' ? 'Kullanıcı adı / E-posta' : 'Username / Email'}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={language === 'tr' ? 'kullanıcı@örnek.com' : 'you@example.com'}
                required
                autoComplete="username"
                className="input-cinematic"
              />
            </div>

            <div>
              <label className="field-label">{t.password}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="input-cinematic pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  className="absolute right-0 bottom-3 w-10 h-10 flex items-center justify-center text-ink-mute hover:text-ink transition-colors"
                >
                  {showPassword ? (
                    <VisibilityOff style={{ width: '18px', height: '18px' }} />
                  ) : (
                    <Visibility style={{ width: '18px', height: '18px' }} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-accent w-full justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t.loginInProgress : t.loginButton}
              <span aria-hidden>→</span>
            </button>
          </form>

          {SHOW_GOOGLE_AUTH && (
            <>
              {/* Divider */}
              <div className="my-8 section-rule">
                <span>{language === 'tr' ? 'veya' : 'or'}</span>
              </div>

              {/* Google */}
              <button
                onClick={handleGoogleLogin}
                disabled={googleLoading || loading}
                className="btn-ghost w-full justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {googleLoading
                  ? (language === 'tr' ? 'Yönlendiriliyor…' : 'Redirecting…')
                  : (language === 'tr' ? 'Google ile Devam' : 'Continue with Google')}
              </button>
            </>
          )}

          {/* Footer line */}
          <p className="mt-10 text-center font-prose text-[14px] text-ink-dim">
            {language === 'tr' ? 'Hesabın yok mu? ' : "Don't have an account? "}
            <Link to="/register" className="text-accent hover:underline underline-offset-2">
              {language === 'tr' ? 'Kayıt ol' : 'Sign up'}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
